// Filter-state → GraphQL filter-input helpers, shared by every list that has a filter panel.

/** Panel selections plus an optional inventory extraFilters; all-empty stays `undefined`. */
const mergeFilters = (input, extraFilters) => {
  const merged = { ...input, ...(extraFilters ?? {}) };
  return Object.values(merged).some(Boolean) ? merged : undefined;
};

/** A multi-select sends [] when nothing is picked; the server wants the key absent. */
export const listFilter = (values) => (values?.length ? values : undefined);

/** An id multi-select holds strings; the `*Id` filter inputs are `[Int!]`. */
export const idListFilter = (values) => listFilter(values?.map(Number));

/** `[FilterOption]` → a filter panel's options; string values, as `idListFilter` reads them back. */
export const processFilterOptions = (items) =>
  (items ?? []).map((item) => ({ value: String(item.id), label: item.name }));

/** `DateRangeFilter` takes a preset OR a from/to pair, never both. */
export const dateRangeFilter = (range) => {
  if (range?.preset) return { preset: range.preset };
  if (range?.from || range?.to) {
    return { from: range.from || undefined, to: range.to || undefined };
  }
  return undefined;
};

// The server's DateRangePreset enum.
export const DATE_RANGE_PRESETS = Object.freeze({
  last7: 'LAST_7',
  last30: 'LAST_30',
  last60: 'LAST_60',
});

// Every key is one ContractFilters accepts; an empty selection is omitted, not sent empty.
export const buildContractFiltersInput = (filters = {}, extraFilters) => {
  const input = {
    status: listFilter(filters.status),
    materialId: idListFilter(filters.materialId),
    supplierAccountId: idListFilter(filters.supplierAccountId),
    countryOfOrigin: listFilter(filters.countryOfOrigin),
    rawMaterialType: listFilter(filters.rawMaterialType),
    feedstockCategory: listFilter(filters.feedstockCategory),
    preferredAllocation: listFilter(filters.preferredAllocation),
    deliverBy: dateRangeFilter(filters.deliverBy),
  };
  return mergeFilters(input, extraFilters);
};

// The same, for DeclarationFilters — which spells allocation `preferredAllocation`.
export const buildDeclarationFiltersInput = (filters = {}, extraFilters) => {
  const input = {
    status: listFilter(filters.status),
    materialId: idListFilter(filters.materialId),
    sentBySupplierId: idListFilter(filters.sentBySupplierId),
    declarationVerificationStatus: listFilter(filters.declarationVerificationStatus),
    countryOfOrigin: listFilter(filters.countryOfOrigin),
    rawMaterialType: listFilter(filters.rawMaterialType),
    feedstockCategory: listFilter(filters.feedstockCategory),
    preferredAllocation: listFilter(filters.preferredAllocation),
    ghgEmissionSavings: listFilter(filters.ghgEmissionSavings),
    dateOfDispatch: dateRangeFilter(filters.dateOfDispatch),
    dateOfDischarge: dateRangeFilter(filters.dateOfDischarge),
  };
  return mergeFilters(input, extraFilters);
};

// The same, for ShipmentFilters. `ShipmentFilters.receivedStatus` was renamed to `status`.
export const buildShipmentFiltersInput = (filters = {}, extraFilters) => {
  const input = {
    status: listFilter(filters.status),
    materialId: idListFilter(filters.materialId),
    supplierId: idListFilter(filters.supplierId),
    countryOfOrigin: listFilter(filters.countryOfOrigin),
    rawMaterialType: listFilter(filters.rawMaterialType),
    feedstockCategory: listFilter(filters.feedstockCategory),
    allocationPreference: listFilter(filters.allocationPreference),
    dateOfDispatch: dateRangeFilter(filters.dateOfDispatch),
    dateOfDischarge: dateRangeFilter(filters.dateOfDischarge),
  };
  return mergeFilters(input, extraFilters);
};

// `PendingOrderFilters` spells the order's own status `orderStatus`.
export const buildPendingOrderFiltersInput = (filters = {}, extraFilters) =>
  mergeFilters(
    {
      orderStatus: listFilter(filters.status),
      materialId: idListFilter(filters.materialId),
      customerAccountId: idListFilter(filters.customerAccountId),
    },
    extraFilters,
  );

// `OutgoingShipmentFilters` — the panel's material and customer, plus the inventory filters.
export const buildOutgoingShipmentFiltersInput = (filters = {}, extraFilters) =>
  mergeFilters(
    {
      status: listFilter(filters.status),
      materialId: idListFilter(filters.materialId),
      customerAccountId: idListFilter(filters.customerAccountId),
    },
    extraFilters,
  );

// `FulfillmentFilters` — the panel's allocation preference, material and customer.
export const buildFulfillmentFiltersInput = (filters = {}, extraFilters) =>
  mergeFilters(
    {
      allocationPreference: listFilter(filters.allocationPreference),
      materialId: idListFilter(filters.materialId),
      customerAccountId: idListFilter(filters.customerAccountId),
    },
    extraFilters,
  );

// `TransactionFilters` — the ledger's own facets; the inventory filters key by terminal NAME.
export const buildTransactionFiltersInput = (filters = {}, extraFilters) =>
  mergeFilters(
    {
      type: listFilter(filters.type),
      allocationPreference: listFilter(filters.allocationPreference),
      materialId: idListFilter(filters.materialId),
      counterpartyAccountId: idListFilter(filters.counterpartyAccountId),
    },
    extraFilters,
  );
